import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  productionReadinessApi,
  type BusinessJourney,
  type ChecklistItem,
  type GoNoGoItem,
  type IntegrationPoint,
  type PRTTimelineEvent,
  type ReadinessDashboardSnapshot,
  type ReadinessReport,
  type RiskItem,
} from '../../api/production-readiness-prt001-runtime';

type Tab = 'dashboard' | 'integration' | 'journeys' | 'reports' | 'go_nogo' | 'checklist' | 'risks' | 'timeline';

export function ProductionReadinessPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<ReadinessDashboardSnapshot | null>(null);
  const [integration, setIntegration] = useState<IntegrationPoint[]>([]);
  const [journeys, setJourneys] = useState<BusinessJourney[]>([]);
  const [reports, setReports] = useState<ReadinessReport[]>([]);
  const [goNoGo, setGoNoGo] = useState<GoNoGoItem[]>([]);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [risks, setRisks] = useState<RiskItem[]>([]);
  const [timeline, setTimeline] = useState<PRTTimelineEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-emerald-200 bg-white dark:border-emerald-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-emerald-700 dark:text-emerald-300';
  const shell = 'bg-emerald-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, integ, jour, gng, chk, rsk, tl] = await Promise.all([
        productionReadinessApi.dashboard(), productionReadinessApi.integration(),
        productionReadinessApi.journeys(), productionReadinessApi.goNoGo(),
        productionReadinessApi.releaseChecklist(), productionReadinessApi.riskRegister(),
        productionReadinessApi.history(),
      ]);
      const reps = await Promise.all(['readiness', 'performance', 'security', 'scalability', 'pilot'].map((t) => productionReadinessApi.report(t)));
      setDashboard(dash); setIntegration(integ); setJourneys(jour); setReports(reps);
      setGoNoGo(gng); setChecklist(chk); setRisks(rsk); setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load production readiness');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Overview' }, { id: 'integration', label: 'Integration' },
    { id: 'journeys', label: 'Journeys' }, { id: 'reports', label: 'Reports' },
    { id: 'go_nogo', label: 'Go/No-Go' }, { id: 'checklist', label: 'Release Checklist' },
    { id: 'risks', label: 'Risk Register' }, { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>PRT-000001 · Production Readiness · Release Blocker</p>
            <h1 className="text-xl font-semibold">Production Readiness Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Overall score <strong className={accent}>{dashboard.overall_score}</strong></span>
                <span>Integration <strong className={accent}>{dashboard.integration_score}%</strong></span>
                <span>Journey pass rate <strong className={accent}>{dashboard.journey_pass_rate}%</strong></span>
                <span>RC <strong className={accent}>{dashboard.release_candidate}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/marketplace-brain" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Operating Brain</Link>
            <Link to="/platform/production-readiness-prt001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Program Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading production readiness data…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-emerald-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Overall Score', value: dashboard.overall_score },
            { label: 'Critical Bugs', value: dashboard.critical_bugs },
            { label: 'Security Findings', value: dashboard.open_security_findings },
            { label: 'Pilot Ready', value: dashboard.pilot_ready ? 'Yes' : 'No' },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'integration' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {integration.map((p) => (
            <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex justify-between"><p className="font-semibold">{p.name}</p><span className="text-xs text-emerald-600">{p.status}</span></div>
              <p className={`text-xs ${muted}`}>{p.service} · {p.interface}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'journeys' && journeys.map((j) => (
        <div key={j.key} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{j.name}</p><span className={`text-sm ${accent}`}>{j.pass_rate_pct}% pass</span></div>
          <p className={`mt-1 text-xs ${muted}`}>{j.steps.join(' → ')}</p>
        </div>
      ))}

      {tab === 'reports' && reports.map((r) => (
        <div key={r.type} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{r.title}</p><span className={`text-sm ${accent}`}>Score {r.score}</span></div>
          <p className={`mt-2 text-sm ${muted}`}>{r.summary}</p>
        </div>
      ))}

      {tab === 'go_nogo' && goNoGo.map((g) => (
        <div key={g.key} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between">
            <p className="font-semibold">{g.criterion}</p>
            <span className={`text-sm uppercase ${g.status === 'go' ? 'text-emerald-600' : 'text-red-600'}`}>{g.status}</span>
          </div>
          {g.owner && <p className={`text-xs ${muted}`}>Owner: {g.owner}{g.blocking ? ' · Blocking' : ''}</p>}
        </div>
      ))}

      {tab === 'checklist' && checklist.map((c) => (
        <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between">
            <p className="font-semibold">{c.title}</p>
            <span className={`text-sm ${c.completed ? 'text-emerald-600' : muted}`}>{c.completed ? 'Done' : 'Pending'}</span>
          </div>
          <p className={`text-xs ${muted}`}>{c.category}</p>
        </div>
      ))}

      {tab === 'risks' && risks.map((r) => (
        <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{r.title}</p><span className="text-xs uppercase">{r.severity}</span></div>
          {r.mitigation && <p className={`mt-1 text-sm ${muted}`}>{r.mitigation}</p>}
        </div>
      ))}

      {tab === 'timeline' && timeline.map((e) => (
        <div key={e.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{e.title}</p>
          <p className={`text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()} · {e.actor}</p>
        </div>
      ))}
    </div>
  );
}
